import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Avatar, Card, CardContent, Chip, Divider, Stack } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import StarIcon from '@mui/icons-material/Star';

const Profile: React.FC = () => {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserData = async () => {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [user?.uid]);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString();
    }
    if (typeof date === 'string') {
      const d = new Date(date);
      return isNaN(d.getTime()) ? 'N/A' : d.toLocaleDateString();
    }
    return 'N/A';
  };

  const subscription = userData?.subscription;

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(180deg, rgba(15, 15, 20, 0.95) 0%, rgba(25, 25, 35, 0.98) 100%)',
      position: 'relative',
      overflowX: 'hidden'
    }}>
      {/* Background Image with Overlay */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/martinbg2.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        opacity: 0.15,
        zIndex: 0
      }} />
      
      {/* Main Container */}
      <Box sx={{
        width: '100%', 
        maxWidth: 1400,
        mx: 'auto',
        position: 'relative',
        zIndex: 1,
        pl: { xs: 2, md: 4 },
        pr: { xs: 2.5, md: 4 },
        overflowX: 'hidden',
        boxSizing: 'border-box',
        py: { xs: 4, md: 6 }
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: { xs: 4, md: 6 },
          gap: 2
        }}>
          <IconButton 
            onClick={handleBack} 
            sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              bgcolor: 'rgba(227, 28, 37, 0.15)',
              border: '1px solid rgba(227, 28, 37, 0.3)',
              backdropFilter: 'blur(10px)',
              '&:hover': { 
                bgcolor: 'rgba(227, 28, 37, 0.25)',
                borderColor: 'rgba(227, 28, 37, 0.5)',
                transform: 'translateX(-2px)'
              },
              transition: 'all 0.2s ease',
              borderRadius: 2
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h1" sx={{ 
            fontWeight: 300, 
            color: '#ffffff',
            fontSize: { xs: '2rem', md: '4rem' },
            letterSpacing: '-0.03em',
            lineHeight: 1.1,
            wordWrap: 'break-word',
            overflowWrap: 'break-word',
            maxWidth: '100%'
          }}>
            {t('profile.profile')}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            py: 8
          }}>
            <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {t('common.loading')}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: { xs: 3, md: 4 }
          }}>
            {/* User Info Card */}
            <Card sx={{
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 3,
              p: { xs: 3, md: 4 }
            }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                  <Avatar 
                    src={user?.photoURL || undefined} 
                    alt={user?.displayName || 'U'} 
                    sx={{ 
                      width: { xs: 100, md: 120 }, 
                      height: { xs: 100, md: 120 },
                      bgcolor: '#E53935',
                      mb: 2,
                      border: '3px solid rgba(229, 57, 53, 0.3)'
                    }} 
                  />
                  <Typography variant="h4" sx={{ 
                    color: '#ffffff', 
                    fontWeight: 600,
                    mb: 1,
                    textAlign: 'center',
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    maxWidth: '100%',
                    px: { xs: 1, md: 0 }
                  }}>
                    {user?.displayName || userData?.displayName || userData?.name || user?.email}
                  </Typography>
                  {userData?.role && (
                    <Chip 
                      label={userData.role.charAt(0).toUpperCase() + userData.role.slice(1)} 
                      sx={{ 
                        bgcolor: userData.role === 'admin' ? 'rgba(229, 57, 53, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                        color: userData.role === 'admin' ? '#E53935' : 'rgba(255, 255, 255, 0.9)',
                        border: userData.role === 'admin' ? '1px solid rgba(229, 57, 53, 0.3)' : '1px solid rgba(255, 255, 255, 0.15)',
                        fontWeight: 600
                      }}
                    />
                  )}
                </Box>

                <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <EmailIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 20 }} />
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                        {t('profile.email')}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 500, wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                        {user?.email || userData?.email || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>

                  {user?.displayName || userData?.displayName || userData?.name ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <PersonIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 20 }} />
                      <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                        {t('profile.displayName')}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 500, wordWrap: 'break-word', overflowWrap: 'break-word', maxWidth: '100%' }}>
                        {user?.displayName || userData?.displayName || userData?.name || 'N/A'}
                      </Typography>
                      </Box>
                    </Box>
                  ) : null}

                  {userData?.createdAt && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CalendarTodayIcon sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: 20 }} />
                      <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
                        {t('profile.memberSince')}
                      </Typography>
                        <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 500 }}>
                          {formatDate(userData.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Subscription Card */}
            <Card sx={{
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 3,
              p: { xs: 3, md: 4 }
            }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <StarIcon sx={{ color: '#E53935', fontSize: 28 }} />
                  <Typography variant="h5" sx={{ 
                    color: '#ffffff', 
                    fontWeight: 600,
                    fontSize: { xs: '1.25rem', md: '1.5rem' }
                  }}>
                    {t('profile.subscription')}
                  </Typography>
                </Box>

                <Divider sx={{ bgcolor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

                {subscription?.active ? (
                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 0.5 }}>
                        {t('common.status')}
                      </Typography>
                      <Chip 
                        label={t('common.active')}
                        color="success"
                        sx={{ 
                          bgcolor: 'rgba(76, 175, 80, 0.2)',
                          color: '#4CAF50',
                          border: '1px solid rgba(76, 175, 80, 0.3)',
                          fontWeight: 600
                        }}
                      />
                    </Box>

                    {subscription.plan && (
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 0.5 }}>
                          {t('shop.plan')}
                        </Typography>
                        {subscription.isAdminTrainer ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              label={role === 'admin' ? t('profile.adminUnlimited') : role === 'trainer' ? t('profile.trainerUnlimited') : t('profile.unlimitedAccess')}
                              sx={{
                                bgcolor: 'rgba(229, 57, 53, 0.2)',
                                color: '#E53935',
                                border: '2px solid rgba(229, 57, 53, 0.4)',
                                fontWeight: 700,
                                fontSize: { xs: '0.75rem', md: '0.875rem' },
                                px: 1,
                                py: 0.5
                              }}
                            />
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontStyle: 'italic' }}>
                              {t('profile.allModulesUnlocked')}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 500 }}>
                            {subscription.plan === '1month' ? '1 Month' :
                             subscription.plan === '3months' ? '3 Months' :
                             subscription.plan === '6months' ? '6 Months' :
                             subscription.plan === '12months' ? '12 Months' :
                             subscription.plan}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {(subscription.endDate || subscription.isAdminTrainer) && (
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 0.5 }}>
                          {t('shop.expires')}
                        </Typography>
                        <Typography variant="body1" sx={{ color: '#ffffff', fontWeight: 500 }}>
                          {subscription.isAdminTrainer ? t('profile.noExpiry') : (subscription.endDate ? formatDate(subscription.endDate) : t('profile.noExpiry'))}
                        </Typography>
                      </Box>
                    )}

                    {subscription.unlockedModules && subscription.unlockedModules.length > 0 && (
                      <Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 1 }}>
                        {t('profile.unlockedModules')}
                      </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {subscription.unlockedModules.map((module: number) => (
                            <Chip
                              key={module}
                              label={t('dashboard.moduleLabel', { number: module })}
                              sx={{
                                bgcolor: 'rgba(229, 57, 53, 0.15)',
                                color: '#E53935',
                                border: '1px solid rgba(229, 57, 53, 0.3)',
                                fontWeight: 600,
                                fontSize: { xs: '0.7rem', md: '0.75rem' }
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {subscription.history && subscription.history.length > 0 && (
                      <Box>
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mb: 1 }}>
                          {t('profile.history')}
                        </Typography>
                        <Stack spacing={1}>
                          {subscription.history.slice(-5).reverse().map((entry: any, idx: number) => (
                            <Box key={idx} sx={{ 
                              p: { xs: 1, md: 1.5 }, 
                              bgcolor: 'rgba(255, 255, 255, 0.05)',
                              borderRadius: 1,
                              border: '1px solid rgba(255, 255, 255, 0.08)'
                            }}>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', fontSize: { xs: '0.65rem', md: '0.75rem' }, wordWrap: 'break-word', overflowWrap: 'break-word' }}>
                                {entry.action === 'granted' ? t('profile.granted') :
                                 entry.action === 'extended' ? t('profile.extended') :
                                 t('profile.revoked')}{' '}
                                {entry.months} {t('profile.month')}{entry.months > 1 ? 's' : ''}
                                {entry.grantedByName && ` ${t('profile.by')} ${entry.grantedByName}`}
                              </Typography>
                              {entry.timestamp?.seconds && (
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block', mt: 0.5, fontSize: { xs: '0.6rem', md: '0.7rem' } }}>
                                  {formatDate(entry.timestamp)}
                                </Typography>
                              )}
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, fontSize: { xs: '0.9rem', md: '1rem' } }}>
                      {t('profile.noActiveSubscription')}
                    </Typography>
                    <Chip 
                      label={t('profile.inactive')} 
                      sx={{ 
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        color: 'rgba(255, 255, 255, 0.7)',
                        border: '1px solid rgba(255, 255, 255, 0.15)'
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Profile;

